import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Waves, Footprints, GraduationCap, ShoppingBag, Bus, ShieldCheck, MapPin, Navigation, Users } from "lucide-react";
import { useSite, wa } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const UVIP = "/assets/real";

export const UltraVipShowcase = () => {
  const D = useSite();
  const room = (D.rooms || []).find((r) => r.slug === "ultra-vip-2-kisilik");
  if (!room) return null;
  const thumbs = [`${UVIP}/ultra-vip-3.jpg`, `${UVIP}/ultra-vip-2.jpg`, `${UVIP}/ultra-vip-4.jpg`];

  return (
    <section data-testid="ultravip-showcase" className="relative overflow-hidden bg-charcoal-950 py-24 md:py-32">
      <div className="absolute -right-24 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 md:grid-cols-12 md:px-12">
        <div className="md:col-span-6">
          <Reveal>
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative inline-flex items-center gap-2.5 overflow-hidden rounded-full border border-gold/40 bg-gradient-to-r from-gold/20 to-gold/5 px-5 py-2.5 font-accent text-[11px] uppercase tracking-[0.28em] text-gold-light"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-light opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-light" />
              </span>
              Yeni Açıldı
              <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                animate={{ x: ["-120%", "420%"] }}
                transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 2.2, ease: "easeInOut" }}
              />
            </motion.span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-white sm:text-5xl">{room.name}</h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-white/70">{room.desc}</p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {(room.features || []).map((f) => (
                <span key={f} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs text-white/80">
                  {f}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.34}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                data-testid="ultravip-rooms-cta"
                to="/odalar"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-semibold text-charcoal-900 transition-all duration-500 ease-premium hover:-translate-y-0.5 hover:shadow-lift"
              >
                Odayı İncele
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                data-testid="ultravip-whatsapp-cta"
                href={wa(D.site, "Merhaba, yeni açılan Ultra VIP oda hakkında bilgi almak istiyorum.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/30 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur transition-all duration-500 hover:bg-white/15"
              >
                <WhatsAppIcon className="h-4 w-4" /> Yer Ayırt
              </a>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6">
          <Reveal delay={0.15}>
            <div className="relative">
              <div aria-hidden className="absolute -right-4 -top-4 hidden h-full w-full rounded-[2rem] border border-gold/25 md:block" />
              <div className="group relative overflow-hidden rounded-[2rem] shadow-lift">
                <img
                  src={`${UVIP}/ultra-vip-1.jpg`}
                  alt={room.name}
                  loading="lazy"
                  className="h-[340px] w-full object-cover transition-transform duration-[1200ms] ease-premium group-hover:scale-[1.06] md:h-[460px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/55 via-transparent to-transparent" />
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-4 py-2 text-xs font-medium text-white backdrop-blur-md"
                >
                  <Users className="h-3.5 w-3.5" /> Ultra VIP · {room.capacity || "2 Kişi"}
                </motion.div>
              </div>
            </div>
          </Reveal>
          <div className="mt-5 grid grid-cols-3 gap-4">
            {thumbs.map((t, i) => (
              <Reveal key={t} delay={0.25 + i * 0.1}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="group overflow-hidden rounded-2xl shadow-premium ring-1 ring-white/5"
                >
                  <img
                    src={t}
                    alt={`${room.name} ${i + 2}`}
                    loading="lazy"
                    className="h-24 w-full object-cover transition-transform duration-700 ease-premium group-hover:scale-110 md:h-28"
                  />
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PERKS = [
  { icon: Waves, title: "Denize Sıfır", text: "Atatürk Bulvarı üzeri, deniz manzaralı konum" },
  { icon: Footprints, title: "Sahile 2 Dakika", text: "Atakum sahiline yürüme mesafesi" },
  { icon: GraduationCap, title: "Kampüse Yakın", text: "OMÜ ve okullara kolay ulaşım" },
  { icon: Bus, title: "Toplu Ulaşım", text: "Otobüs ve tramvay durakları hemen yanında" },
  { icon: ShoppingBag, title: "Market & Eczane", text: "Günlük ihtiyaçlar birkaç adım ötede" },
  { icon: ShieldCheck, title: "Güvenli Bölge", text: "Aydınlık, işlek ve huzurlu bir çevre" },
];

export const LocationAdvantages = () => {
  const D = useSite();
  const mapQ = D.site?.mapQuery || D.site?.address || "Atakum Samsun";
  const addr = D.site?.address || "";
  return (
    <section id="konum" data-testid="location-advantages" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="grid gap-14 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <SectionHeading
            overline="Konum Avantajları"
            title="Şehrin *tam kalbinde*, denize sıfır"
            description="Ada Kız Öğrenci Yurdu; ulaşımı kolay, sosyal ve güvenli bir çevrede, sahilin hemen yanında konumlanır."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 overflow-hidden rounded-[2rem] shadow-lift">
              <img src={`${UVIP}/konum.jpg`} alt="Yurdun konumu ve çevresi" loading="lazy" className="h-64 w-full object-cover md:h-72" />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7">
          <div className="grid gap-5 sm:grid-cols-2">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  data-testid={`perk-${i}`}
                  className="group h-full rounded-3xl border border-charcoal/5 bg-white p-7 shadow-premium transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold-dark transition-colors duration-300 group-hover:bg-gold/20">
                    <p.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-charcoal-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-16 overflow-hidden rounded-[2rem] border border-charcoal/5 shadow-lift">
          <iframe
            title="Konum haritası"
            data-testid="location-map"
            src={`https://www.google.com/maps?q=${encodeURIComponent(mapQ)}&output=embed`}
            className="h-[360px] w-full md:h-[440px]"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </Reveal>
      <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-sm text-charcoal-900/70">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" strokeWidth={1.5} /> {addr}
        </p>
        <a
          data-testid="directions-cta"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQ)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-charcoal-900 px-7 py-3.5 text-sm font-semibold text-cream-50 transition-all duration-300 ease-premium hover:-translate-y-0.5 hover:bg-gold-dark"
        >
          <Navigation className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" /> Yol Tarifi Al
        </a>
      </div>
    </section>
  );
};
